import { useAppSelector } from '@/redux/hooks'
import classnames from 'classnames/bind'
import { differenceInSeconds } from 'date-fns'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react'

// Libs
import useCheckRoomCanAccess from '@/hooks/livemallclub/useCheckRoomCanAccess'
import useMemberVips from '@/hooks/livemallclub/useMemberVips'
import useVipOnlyPopup from '@/hooks/livemallclub/useVipOnlyPopup'
import { useFavoriteLive } from '@/hooks/useFavoriteLive'
import { useLiveItemRoomData } from '@/hooks/useLiveItemRoomData'
import handleImageBlur from '@/methods/handleImageBlur'
import { enterRoom } from '@/methods/livemallclub/enterLiveRoom'
import { enterLiveRoomDetails } from '@/methods/livemallclub/enterLiveRoomDetails'
import { FeedType } from '@/types/feed'
import { checkIsVip } from '../ExploreFeedLiveItem'

// Components
import Image, { PlaceholderType } from '@/components/common/Image'
import VisibilityMask from '@/components/shared/livemallclub/LiveItem/VisiblityMask'
import LivePreviewDrawer from '@/components/shared/livemallclub/LivePreview'
import FollowHeart from '../FollowHeart'
import VipOnlyMask from '../VipOnlyMask'
import VipTag from './VipTag'

// Constants
import { ImageSizes } from '@/constants/imageSizes'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'
import { ROUTE_PATH } from '@/constants/routePath'
import { ErrorData } from '@/types'

// Types

// Styles
import styles from './style.module.scss'
import VideoStatusTag from '../VideoStatusTag'

const cx = classnames.bind(styles)

const LiveItemPlayer = dynamic(() => import('../../LiveItemPlayer'), {
  ssr: false,
})

type ListItemType = FeedType['meta'] & {
  className?: string
  refetchCurrentPage?: () => void
  showVisibilityMask?: boolean
  isProfile?: boolean
}

const LiveItem = (props: ListItemType) => {
  const {
    className,
    title,
    coverPhotoUrl,
    ownerAvatarUrl,
    ownerName,
    liveCloseTime,
    roomId,
    liveType,
    liveTime,
    roomDesc,
    isFavorite,
    refetchCurrentPage,
    startTime,
    showVisibilityMask = false,
    isProfile = false,
    valid = true,
    isVip: isVipOnly,
    ownerMemberInfoId,
    ownerMerchantId,
    liveSourceType,
  } = props

  const { memberData } = useAppSelector((state) => state.member)
  const { data: vipShopList } = useMemberVips({ memberInfoId: memberData.memberInfoId })
  const [isShowLiveItemPlayer, setIsShowLiveItemPlayer] = useState(false)
  const [isLiveItemInersecting, setIsLiveItemInersecting] = useState(false)
  const [isLivePreviewOpen, setIsLivePreviewOpen] = useState(false)
  const { handleAddFavoriteLive, handleDeleteFavoriteLive } = useFavoriteLive()
  const [isFavoriteState, setIsFavoriteState] = useState(isFavorite)
  const { PopupComponent: VipOnlyPopup, handleOpen: openVipOnlyPopup } = useVipOnlyPopup()

  const handleFavoriteClick = () => {
    try {
      setIsFavoriteState(!isFavoriteState)
      const mutationFunction = isFavoriteState ? handleDeleteFavoriteLive : handleAddFavoriteLive
      mutationFunction({ roomId })
      refetchCurrentPage && refetchCurrentPage()
    } catch (error) {
      setIsFavoriteState(!isFavoriteState)
    }
  }

  const videoRef = useRef(null)
  const liveItemRef: any = useRef(null)

  const router = useRouter()

  const isLive = liveType === ROOM_LIVE_TYPE.CURRENT && !!liveTime
  const isFuture = liveType === ROOM_LIVE_TYPE.FUTURE
  const isPast = liveType === ROOM_LIVE_TYPE.PAST

  const isLivePage = router.pathname === ROUTE_PATH.LIVE
  const isOwnLive = memberData.memberInfoId === ownerMemberInfoId
  const isVip = checkIsVip({ vipShopList: vipShopList ?? [], merchantId: Number(ownerMerchantId) })
  const showVipOnlyMask = isVipOnly && !isVip && !isOwnLive
  const { checkRoomCanAccess } = useCheckRoomCanAccess()

  const isOverCloseTime = differenceInSeconds(new Date(), new Date(liveCloseTime)) > 0

  const { roomLivingData, hasLiveUrl } = useLiveItemRoomData({ roomId, isLive: isLive && !isOverCloseTime, enabled: isLivePage }) // 這裡的 isLive 只單純判斷 liveType 會因為 server side cache 而資料有落差 // 所以多判斷 liveCloseTime

  const currentProfileId = router.query?.id?.[0] ?? memberData.memberInfoId
  const isMyProfile = currentProfileId === memberData.memberInfoId

  useEffect(() => {
    if (!isLive) return

    const options = {
      root: null,
    }

    // 目前每個 LiveItem 自己一個 IntersectionObserver
    // 如果同時直播的數量變多，要考慮一個 IntersectionObserver observe 所有 LiveItem
    const intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsLiveItemInersecting(true)
      } else {
        setIsLiveItemInersecting(false)
      }
    }, options)

    intersectionObserver.observe(liveItemRef.current)

    return () => {
      intersectionObserver.disconnect()
    }
  }, [isLive])

  const handleClick = async (event: ReactMouseEvent<HTMLDivElement>) => {
    if (isLivePreviewOpen || showVisibilityMask) return

    event.stopPropagation()
    try {
      await checkRoomCanAccess(roomId)
      if (isProfile && isMyProfile) {
        isLive ? enterRoom({ roomId, liveType: ROOM_LIVE_TYPE.CURRENT, liveSourceType }) : enterLiveRoomDetails({ id: roomId })
        return
      }
      if (isLive || isPast) {
        enterRoom({ roomId, liveType, liveSourceType })
      } else {
        setIsLivePreviewOpen(true)
      }
    } catch (error) {
      if ((error as ErrorData).status === 403) {
        openVipOnlyPopup()
      }
    }
  }

  return (
    <div onClick={handleClick} className={cx('item', className)} ref={liveItemRef}>
      <a href={`${ROUTE_PATH.LIVE}/${roomId}`} />
      <div className={cx('item-image')}>
        <Image
          src={coverPhotoUrl}
          width={224}
          height={300}
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          alt={title}
          sizes='(max-width: 710px) 60vw, (max-width: 1200px) 50vw, 33vw'
          placeholder={PlaceholderType.blur}
          priority
          blurDataURL={handleImageBlur()}
        />
        {isVipOnly && !showVipOnlyMask && <VipTag />}
        {showVipOnlyMask && <VipOnlyMask />}
        <div className={cx('image-mask__upper')} />

        {isLive && hasLiveUrl && isLiveItemInersecting && isLivePage && (
          <LiveItemPlayer
            className={cx('item-player', { isShow: isShowLiveItemPlayer })}
            wrapperClassName={cx('item-player-wrapper')}
            videoRef={videoRef}
            src={roomLivingData?.liveUrlInfo[0]?.streamUrl}
            sessionRoomId={roomLivingData?.gdata.sessionRoomId}
            isShowLiveItemPlayer={isShowLiveItemPlayer}
            setIsShowLiveItemPlayer={setIsShowLiveItemPlayer}
            liveTime={liveTime}
          />
        )}
        <FollowHeart
          isActive={isFavoriteState}
          className={cx('item__heading-block-follow')}
          forceChangeOnClick
          onClick={handleFavoriteClick}
          showMask
        />
      </div>

      <div className={cx('item-content')}>
        <div className={cx('item-content__header')}>
          <div className={cx('item-logo')}>
            <Image
              src={ownerAvatarUrl}
              width={50}
              height={50}
              style={{ objectFit: 'cover', objectPosition: 'center' }}
              alt={ownerName}
              sizes={ImageSizes.headShot}
            />
          </div>
          <p className={cx('item-content-name')}>{ownerName}</p>
        </div>
        <h3 className={cx('item-content-title')}>{title}</h3>

        <div className={cx('item-content__description')}>{roomDesc}</div>
      </div>
      <div className={cx('item__heading')}>
        <VideoStatusTag
          roomId={roomId}
          typeMap={{
            live: isLive,
            past: isPast,
            future: isFuture,
          }}
          time={isLive ? liveTime : startTime}
        />
      </div>
      <LivePreviewDrawer
        {...props}
        onClose={() => setIsLivePreviewOpen(false)}
        isDrawerOpen={isLivePreviewOpen}
        handleHeartClick={handleFavoriteClick}
        isActive={!!isFavorite}
      />
      {showVisibilityMask && <VisibilityMask initialVisibility={valid} id={roomId} type='room' />}
      <VipOnlyPopup />
    </div>
  )
}
export default LiveItem
